import React, { useState, useEffect, useCallback, useRef } from 'react';
import useTeacherPaceMeter, { TEACHER_PACE_METER_UPDATE_EVENT, TeacherPaceMeterUpdateEvent } from 'hooks/useTeacherPaceMeter';
import './styles.css';

interface TeacherPaceMeterProps {
  classId: number | string;
}

// Helper function to get pace level class
const getPaceLevelClass = (paceScore: number): string => {
  if (paceScore <= 10) return 'slow-pace';
  if (paceScore <= 20) return 'good-pace';
  return 'fast-pace';
};

const TeacherPaceMeter: React.FC<TeacherPaceMeterProps> = ({ classId }) => {
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  
  // Ref to store the meter indicator element
  const meterIndicatorRef = useRef<HTMLDivElement>(null);
  
  // Use a 30-second polling interval for the API
  const { paceMeterData, isLoading, error, refetch } = useTeacherPaceMeter(classId, 30000);

  // Function to update the indicator position directly in the DOM
  const updateIndicatorPosition = useCallback(() => {
    if (!paceMeterData || !meterIndicatorRef.current) return;
    
    // Update position
    const position = Math.max(0, Math.min(100, (paceMeterData.paceScore / 30) * 100));
    meterIndicatorRef.current.style.left = `${position}%`;
    
    // Update class
    const levelClass = getPaceLevelClass(paceMeterData.paceScore);
    meterIndicatorRef.current.className = `meter-indicator ${levelClass}`;
    
    // Update percentage text
    const valueElement = meterIndicatorRef.current.querySelector('.indicator-value');
    if (valueElement) {
      valueElement.textContent = `${paceMeterData.paceScore}/30`;
    }
  }, [paceMeterData]);
  
  // Update indicator position when data changes
  useEffect(() => {
    updateIndicatorPosition();
  }, [updateIndicatorPosition]);
  
  // Listen for teacher pace meter updates
  useEffect(() => {
    const handleTeacherPaceMeterUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<TeacherPaceMeterUpdateEvent>;
      const { paceScore} = customEvent.detail;
      
      if (meterIndicatorRef.current) {
        // Update position
        const position = Math.max(0, Math.min(100, (paceScore / 30) * 100));
        meterIndicatorRef.current.style.left = `${position}%`;
        
        // Update class
        const levelClass = getPaceLevelClass(paceScore);
        meterIndicatorRef.current.className = `meter-indicator ${levelClass}`;
        
        // Update percentage text
        const valueElement = meterIndicatorRef.current.querySelector('.indicator-value');
        if (valueElement) {
          valueElement.textContent = `${paceScore}/30`;
        }
      }
    };
    
    // Add event listener
    document.addEventListener(TEACHER_PACE_METER_UPDATE_EVENT, handleTeacherPaceMeterUpdate);
    
    // Clean up
    return () => {
      document.removeEventListener(TEACHER_PACE_METER_UPDATE_EVENT, handleTeacherPaceMeterUpdate);
    };
  }, []);
  
  // Early return for loading and error states
  if (isLoading) {
    return <div className="teacher-pace-meter loading">Loading pace data...</div>;
  }

  if (error || !paceMeterData) {
    return <div className="teacher-pace-meter error">Unable to load teacher pace data</div>;
  }

  // Calculate the position of the indicator (1-30 scale to 0-100%)
  const indicatorPosition = Math.max(0, Math.min(100, (paceMeterData.paceScore / 30) * 100));
  
  // Get the pace level class
  const paceLevelClass = getPaceLevelClass(paceMeterData.paceScore);
    
  // Check if the pace is in the red zone (25-30)
  const isRedZone = paceMeterData.isRedZone || paceMeterData.paceScore >= 25;

  return (
    <div className={`teacher-pace-meter ${isRedZone ? 'red-zone' : ''}`}>
      <div className="panel-header">
        <div className="panel-title-container">
          <h3 className="panel-title">
            Teacher Pace
          </h3>
          
          <button 
            className={`refresh-button ${isRefreshing ? 'refreshing' : ''}`}
            onClick={() => {
              setIsRefreshing(true);
              refetch().finally(() => {
                setTimeout(() => setIsRefreshing(false), 500);
              });
            }}
            disabled={isRefreshing}
            title="Refresh data"
          >
            {isRefreshing ? '⟳' : '↺'}
          </button>
        </div>
      </div>
      
      <div className="pace-meter-container">
        <div className="pace-meter">
          <div className="meter-scale">
            <div className="meter-section slow-section"></div>
            <div className="meter-section good-section"></div>
            <div className="meter-section fast-section"></div>
          </div>
          <div 
            className={`meter-indicator ${paceLevelClass}`}
            style={{ left: `${indicatorPosition}%` }}
            ref={meterIndicatorRef}
            onMouseEnter={(e) => {
              // Show the value on hover
              const target = e.currentTarget.querySelector('.indicator-value');
              if (target) {
                (target as HTMLElement).style.opacity = '1';
                (target as HTMLElement).style.transform = 'translateX(-50%) translateY(0)';
              }
            }}
            onMouseLeave={(e) => {
              // Hide the value when not hovering
              const target = e.currentTarget.querySelector('.indicator-value');
              if (target) {
                (target as HTMLElement).style.opacity = '0';
                (target as HTMLElement).style.transform = 'translateX(-50%) translateY(4px)';
              }
            }}
          >
            <span className="indicator-value">{paceMeterData.paceScore}/30</span>
          </div>
        </div>
        {/* Pace level label removed as requested */}
        {isRedZone && <div className="red-zone-badge">Too Fast!</div>}
      </div>
    </div>
  );
};

export default TeacherPaceMeter;